import type { AgentRun, DashboardSeries, SeriesPoint } from '@auw/api-client';

export type UsageMetric = 'runs' | 'tokens';

/**
 * 문서 에이전트가 포맷별로 기록하는 workspace_type 들.
 *
 * 백엔드는 `doc_agent_formats.DOC_AGENT_FORMAT_DESCRIPTORS` 에 따라 포맷마다
 * 다른 값을 남긴다 (docx/xlsx/pptx/pdf). 하지만 사용자에겐 "문서 에이전트"
 * 한 페이지이고 그 안에서 네 포맷을 모두 만들고 편집한다 — 사용량을 네
 * 조각으로 쪼개 보여주면 그 페이지를 얼마나 쓰는지 알 수 없다.
 */
const DOC_AGENT_WORKSPACE_TYPES = new Set([
  'docx-agent',
  'xlsx-agent',
  'ppt-agent',
  'pdf-agent',
]);

const DOC_AGENT_CANONICAL = 'doc-agent';

/**
 * 표시용 workspace_type. 문서 에이전트 포맷들을 한 항목으로 접는다.
 *
 * 접는 건 표시 계층에서만 한다 — 원본 기록은 포맷을 유지하므로 나중에
 * 포맷별 분석이 필요해지면 데이터는 그대로 남아 있다.
 */
export function canonicalWorkspaceType(workspaceType: string): string {
  return DOC_AGENT_WORKSPACE_TYPES.has(workspaceType)
    ? DOC_AGENT_CANONICAL
    : workspaceType;
}

function mergeCounts(
  source: Record<string, number> | undefined,
): Record<string, number> | undefined {
  if (!source) return source;
  const merged: Record<string, number> = {};
  for (const [type, value] of Object.entries(source)) {
    const key = canonicalWorkspaceType(type);
    merged[key] = (merged[key] ?? 0) + value;
  }
  return merged;
}

/**
 * 시리즈의 문서 에이전트 포맷들을 하나로 합친 사본.
 *
 * `workspaces` 순서는 원본을 따른다 — 차트 색상이 이 배열의 index 로
 * 결정되므로, 정렬을 바꾸면 기간을 넘길 때 같은 항목의 색이 튄다.
 */
export function mergeDocAgentWorkspaces(series: DashboardSeries): DashboardSeries {
  const workspaces: string[] = [];
  for (const workspace of series.workspaces) {
    const key = canonicalWorkspaceType(workspace);
    if (!workspaces.includes(key)) workspaces.push(key);
  }
  if (workspaces.length === series.workspaces.length) return series;
  return {
    ...series,
    workspaces,
    points: series.points.map((point) => ({
      ...point,
      by_workspace: mergeCounts(point.by_workspace) ?? {},
      tokens_by_workspace: mergeCounts(point.tokens_by_workspace),
    })),
  };
}

export type DashboardDateRangeError =
  | 'incomplete'
  | 'invalid'
  | 'tooLong';

const DATE_VALUE = /^\d{4}-\d{2}-\d{2}$/;

export interface DashboardPaginationDisplay {
  totalPages: number;
  displayedPage: number;
  start: number;
  end: number;
}

/** Footer values for either fresh rows or React Query placeholder rows. */
export function dashboardPaginationDisplay(
  total: number,
  pageSize: number,
  requestedPage: number,
  responseOffset: number | undefined,
  visibleCount: number,
): DashboardPaginationDisplay {
  const safeTotal = Math.max(0, total);
  const safePageSize = Math.max(1, pageSize);
  const offset = Math.max(
    0,
    responseOffset ?? (Math.max(1, requestedPage) - 1) * safePageSize,
  );
  return {
    totalPages: Math.max(1, Math.ceil(safeTotal / safePageSize)),
    displayedPage: Math.floor(offset / safePageSize) + 1,
    start: safeTotal > 0 ? offset + 1 : 0,
    end: Math.min(offset + Math.max(0, visibleCount), safeTotal),
  };
}

/** Validate the inclusive date range accepted by the dashboard API. */
export function validateDashboardDateRange(
  start: string,
  end: string,
  maxDays = 366,
): DashboardDateRangeError | null {
  if (!start || !end) return 'incomplete';
  if (!DATE_VALUE.test(start) || !DATE_VALUE.test(end)) return 'invalid';

  const startMs = Date.parse(`${start}T00:00:00Z`);
  const endMs = Date.parse(`${end}T00:00:00Z`);
  if (
    !Number.isFinite(startMs)
    || !Number.isFinite(endMs)
    || new Date(startMs).toISOString().slice(0, 10) !== start
    || new Date(endMs).toISOString().slice(0, 10) !== end
    || startMs > endMs
  ) {
    return 'invalid';
  }

  const inclusiveDays = Math.floor((endMs - startMs) / 86_400_000) + 1;
  return inclusiveDays > maxDays ? 'tooLong' : null;
}

export function runDisplayQuery(
  run: Pick<AgentRun, 'query_preview' | 'title'>,
): string | null {
  return run.query_preview ?? run.title;
}

export function formatDashboardTokenCount(
  value: number | null | undefined,
): string {
  if (value == null) return '—';
  const safe = Math.max(0, value);
  const units = [
    { threshold: 1_000, suffix: 'K' },
    { threshold: 1_000_000, suffix: 'M' },
    { threshold: 1_000_000_000, suffix: 'B' },
  ] as const;
  let unitIndex = -1;
  for (let index = 0; index < units.length; index += 1) {
    if (safe >= units[index].threshold) unitIndex = index;
  }
  if (unitIndex < 0) return String(Math.round(safe));
  let unit = units[unitIndex];
  let compact = Math.round((safe / unit.threshold) * 10) / 10;
  // Avoid values such as 1000K after rounding at a unit boundary.
  if (compact >= 1_000 && unitIndex < units.length - 1) {
    unit = units[unitIndex + 1];
    compact = Math.round((safe / unit.threshold) * 10) / 10;
  }
  return `${compact}${unit.suffix}`;
}

export function selectUsagePointMetric(
  point: SeriesPoint,
  metric: UsageMetric,
): { total: number; byWorkspace: Record<string, number>; hasData: boolean } {
  return metric === 'tokens'
    ? {
        total: point.total_token_count ?? 0,
        byWorkspace: point.tokens_by_workspace ?? {},
        hasData: point.total_token_count != null,
      }
    : { total: point.total, byWorkspace: point.by_workspace, hasData: true };
}

/** 사용량 막대 한 칸의 폭(px). 막대 자체는 늘 같은 굵기다.
 *  월별 눈금("26년 7월")은 일·주 눈금("7. 1.")보다 두 배 이상 넓어서, 막대 폭에
 *  맞춘 칸을 그대로 쓰면 이웃 눈금과 겹쳐 "26년 7월26년 8월" 로 붙어 읽힌다. */
export const USAGE_BAR_WIDTH = 22;
const USAGE_MONTH_SLOT_WIDTH = 56;

export function usageColumnWidth(granularity: DashboardSeries['granularity']): number {
  return granularity === 'month' ? USAGE_MONTH_SLOT_WIDTH : USAGE_BAR_WIDTH;
}

/** 활동 격자의 월 라벨. 기간이 달 중간에서 시작하면 첫 달은 한두 주만 보이는데,
 *  그 라벨이 다음 달 라벨과 몇 칸 차이로 붙어 "9월10월" 이 된다. 앞 라벨과
 *  `minGapWeeks` 주보다 가까우면 짧게 잘린 앞 달 라벨을 버린다. */
export function thinMonthMarks<T extends { week: number }>(
  marks: readonly T[],
  minGapWeeks = 3,
): T[] {
  const kept: T[] = [];
  for (const mark of marks) {
    const previous = kept[kept.length - 1];
    if (previous && mark.week - previous.week < minGapWeeks) kept.pop();
    kept.push(mark);
  }
  return kept;
}

/** 사용량 그래프 눈금. 일·주별은 "8/20" 으로 짧게 쓴다.
 *  ko 로케일의 기본 날짜 형식("8. 20.")은 막대 한 칸(22px)보다 넓고 끝의 마침표 때문에
 *  글자 중심이 왼쪽으로 쏠려, 이웃 눈금과 붙고 어느 막대의 날짜인지 헷갈린다. */
export function formatUsageTick(
  bucket: string,
  granularity: DashboardSeries['granularity'],
  locale: string,
): string {
  const date = new Date(`${bucket}T00:00:00Z`);
  if (granularity === 'month') {
    return new Intl.DateTimeFormat(locale, { year: '2-digit', month: 'short', timeZone: 'UTC' }).format(date);
  }
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
}
